import { FC, useEffect, useState } from 'react'
import SettingHorizontalIcon from '@rsuite/icons/SettingHorizontal'
import ListIcon from '@rsuite/icons/List'
import ExitIcon from '@rsuite/icons/Exit'
import TimeIcon from '@rsuite/icons/Time'
import GlobalIcon from '@rsuite/icons/Global'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import throttle from 'lodash/throttle'

import SideNavBar, { NavItemProps } from '../Layouts/SideNavBar'
import OrderListIcon from '../Icons/SavedProducts/CatalogIcon'
import { getRefDimension } from '../../utils/util'
import useUrls from '../../services/useUrls'
import { scrollToRef } from '../../utils/scroll'
import { ProfileRoutes } from '../../utils/constants'
import { BuyerProfileNavigationRefs } from '../../utils/types/buyerProfileTypes'
import { useCustomer } from './Account/CustomerProvider'
import { selectNormalizedCustomer } from './Account/selectors'
import HeartIcon from '../Icons/SavedProducts/HeartIcon'
import ChatIcon from '../Icons/ChatIcon'

import styles from '../../styles/BuyerPortal/BuyerProfileLayout.module.less'

interface BuyerSideNavProps {
  navigationRefs?: BuyerProfileNavigationRefs
}

export type EventKeys = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '11' | '12' | '13' | '14'

export const sideNavItems: NavItemProps<EventKeys>[] = [{
  label: 'buyerProfile:My account',
  eventKey: '1',
  route: 'my-account',
  icon: SettingHorizontalIcon,
},
{
  label: 'buyerProfile:Account information',
  eventKey: '11',
  route: 'my-account/information',
},
{
  label: 'buyerProfile:Delivery details',
  eventKey: '12',
  route: 'my-account/shipping',
},
{
  label: 'buyerProfile:Billing information',
  eventKey: '13',
  route: 'my-account/billing',
},
{
  label: 'buyerProfile:Users',
  eventKey: '14',
  route: 'my-account/users',
},
{
  label: 'buyerProfile:Saved products',
  eventKey: '2',
  route: 'my-products',
  icon: HeartIcon,
},
{
  label: 'buyerProfile:My catalogs',
  eventKey: '3',
  route: 'my-catalogs',
  classes: 'catalog-icon',
  icon: OrderListIcon,
  testId: 'my-catalogs-nav-item',
},
{
  label: 'buyerProfile:Imported products',
  eventKey: '8',
  route: 'my-imported-products',
  icon: GlobalIcon,
},
{
  label: 'buyerProfile:Previously ordered products',
  eventKey: '4',
  route: 'my-ordered-products',
  icon: TimeIcon,
},
{
  label: 'buyerProfile:Order history',
  eventKey: '5',
  route: 'my-orders',
  icon: ListIcon,
},
{
  label: 'common:Contact us',
  eventKey: '6',
  isContactModel: true,
  icon: ChatIcon,
  classes: 'contact-icon',
},
{
  label: 'buyerProfile:Log out',
  eventKey: '7',
  route: 'logout',
  icon: ExitIcon,
  classes: styles['sidenav-logout'],
}]

const BuyerSideNav: FC<BuyerSideNavProps> = ({ navigationRefs }) => {
  const [activeTab, setActiveTab] = useState('1')

  const { asPath, route, locale } = useRouter()
  const { t } = useTranslation('url')
  const { pushT } = useUrls()

  const { customerResponse } = useCustomer()
  const { email } = selectNormalizedCustomer(customerResponse)

  // Setting the active tab on url change
  useEffect(() => {
    if (asPath === `/${t('my-account')}` || asPath === `/${t('my-account')}/${t('information')}`) {
      setActiveTab('11')
    }

    switch (asPath) {
      case `/${t('my-account')}/${t('shipping')}`:
        setActiveTab('12')
        break

      case `/${t('my-account')}/${t('billing')}`:
        setActiveTab('13')
        break

      case `/${t('my-account')}/${t('users')}`:
        setActiveTab('14')
        break

      default:
        break
    }

    setActiveTab(ProfileRoutes[route]?.index ?? '1')
  }, [asPath])

  // Scrolling to correct section after changing the locale
  useEffect(() => {
    let InformationRefHeight = 0
    let deliveryRefHeight = 0
    let billingRefHeight = 0

    setTimeout(() => {
      const topSectionOffset = 300
      const deliveryOffset = 150

      InformationRefHeight = getRefDimension('height', navigationRefs?.informationRef) + topSectionOffset
      deliveryRefHeight = getRefDimension('height', navigationRefs?.deliveryRef) + deliveryOffset
      billingRefHeight = getRefDimension('height', navigationRefs?.billingRef)
    }, 10)

    const triggerIsUnderSelectedElement = throttle(() => {
      const currentScrollY = window.scrollY
      let selectTab: EventKeys = '11'

      const accountUrls = {
        11: 'my-account/information',
        12: 'my-account/shipping',
        13: 'my-account/billing',
        14: 'my-account/users',
      }

      if (currentScrollY > InformationRefHeight) {
        selectTab = '12'
      }

      if (currentScrollY > (InformationRefHeight + deliveryRefHeight)) {
        selectTab = '13'
      }

      if (currentScrollY > (InformationRefHeight + deliveryRefHeight + billingRefHeight)) {
        selectTab = '14'
      }

      pushT(`${accountUrls[selectTab]}`, undefined, { shallow: true })
      setActiveTab(selectTab)
    }, 500)

    if (route === '/my-account') {
      window.addEventListener('scroll', triggerIsUnderSelectedElement)
    }
    return () => window.removeEventListener('scroll', triggerIsUnderSelectedElement)
  }, [locale, email]) // Email deps because it will recalculate height after customer api loads

  const translatedSideNavItems = sideNavItems.map((navItem) => {
    const checkIsAccountItem = () => {
      if (navItem.eventKey === '1' && (['11', '12', '13', '14'].includes(activeTab))) {
        return activeTab
      }

      return navItem.eventKey
    }

    return ({
      ...navItem,
      eventKey: checkIsAccountItem(),
      route: navItem.route?.split('/').map((url) => t(url)).join('/'),
    })
  })
    .filter((item) => (process.env.NEXT_PUBLIC_IMPORTED_PRODUCTS_FEATURE_FLAG === 'true' || item.eventKey !== '8'))

  const handleSelect = (key: string | undefined) => {
    if (key) setActiveTab(key)

    if (route === '/my-account') {
      switch (key) {
        case '11':
          scrollToRef(navigationRefs?.informationRef, 150)
          break

        case '12':
          scrollToRef(navigationRefs?.deliveryRef, 150)
          break

        case '13':
          scrollToRef(navigationRefs?.billingRef, 150)
          break

        case '14':
          scrollToRef(navigationRefs?.userRef, 150)
          break

        default:
          break
      }
    }
  }

  return (
    <div className={styles.sidenav}>
      <SideNavBar
        activeTab={activeTab}
        handleSelect={handleSelect}
        navItems={translatedSideNavItems}
        showUser={false}
      />
    </div>
  )
}

export default BuyerSideNav
